import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { timeOffByNurseIdOf } from "../contexts/WeekView/buildPtoByDayISO";
import { backendService } from "../services/backend.service";
import type { Nurse, NurseRdoDays, SpecificPreference } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";
import { timeOffHistoryKeys } from "./scheduleCache";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

const SECOND_MS = 1000;
// Time-off edits here mark them stale; this only covers changes made elsewhere.
const TIME_OFF_HISTORY_STALE_TIME_MS = 60 * SECOND_MS;
const NO_TIME_OFF: SpecificPreference[] = [];

/**
 * Each department member's RDO days, counted by the server over their whole history, for the nurse drawer. With
 * `draftStart`, only the time off inside the demo's draft weeks from it. Asks only while `enabled`.
 */
export const useRdoDays = (draftStart: DateTime | null, enabled: boolean) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<NurseRdoDays>({
		queryKey: timeOffHistoryKeys.rdoDays(departmentId, draftStart === null ? null : draftStart.toISO()),
		queryFn: async () => (await backendService.getRdoDays(departmentId, draftStart)).data,
		enabled,
		staleTime: TIME_OFF_HISTORY_STALE_TIME_MS,
	});
};

/** The department's time off of two weeks or more still running on `fromDate`, for the Master Schedule. Asks only while `enabled`. */
export const useLongTimeOff = (fromDate: DateTime, enabled: boolean) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const isoDate = toIsoDate(fromDate);
	return useQuery<SpecificPreference[]>({
		queryKey: timeOffHistoryKeys.longTimeOff(departmentId, isoDate),
		queryFn: async () => (await backendService.getLongTimeOff(departmentId, isoDate)).data,
		enabled,
		staleTime: TIME_OFF_HISTORY_STALE_TIME_MS,
	});
};

/**
 * `getNurseById` with each nurse's long time off (two weeks or more, still running on `fromDate`) as their time off:
 * what a Master Schedule grid marks its shifts from, since its weeks run past every week the app has. With the cache
 * on, the server's list; off, the nurse as the old list has them.
 */
export const useNurseWithLongTimeOff = (getNurseById: (nurseId: number) => Nurse | undefined, fromDate: DateTime): ((nurseId: number) => Nurse | undefined) => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const longTimeOff = useLongTimeOff(fromDate, isCacheEnabled);
	return useMemo(() => {
		if (!isCacheEnabled) return getNurseById;
		// DEFAULT: no marks while the list loads, or when it failed (which the Long PTO button says).
		const byNurseId = timeOffByNurseIdOf(longTimeOff.data ?? NO_TIME_OFF);
		return (nurseId: number) => {
			const nurse = getNurseById(nurseId);
			// DEFAULT: a nurse with no long time off has none to mark.
			return nurse && { ...nurse, employeeSpecificPreferences: byNurseId.get(nurseId) ?? NO_TIME_OFF };
		};
	}, [isCacheEnabled, getNurseById, longTimeOff.data]);
};

/** Marks the department's time-off answers stale after a time-off edit, so the screens showing them ask again. */
export const useInvalidateTimeOffHistory = (): (() => void) => {
	const queryClient = useQueryClient();
	const { chosenDepartment } = useChosenDepartment();
	return useCallback(
		() => void queryClient.invalidateQueries({ queryKey: timeOffHistoryKeys.department(chosenDepartment.department_id) }),
		[queryClient, chosenDepartment.department_id],
	);
};
